import { Table as TableType, Row, RowSelectionState } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ReactNode, useEffect } from 'react';
import { TableBody } from '../styles/style';
import { TableRow } from './TableRow';

export const VirtualTableBody = <T,>({
    table,
    rows,
    getScrollElement,
    isLoading,
    rowSelection,
    rowSelectionEnabled,
    groupDisplayMode,
    fixedFirstColumn = false,
    fixedLastColumn = false,
    renderSubcomponent,
}: {
    table: TableType<T>;
    rows: Row<T>[];
    getScrollElement: () => HTMLElement | null;
    isLoading?: boolean;
    rowSelection?: RowSelectionState;
    rowSelectionEnabled: boolean;
    groupDisplayMode: 'groupRows';
    fixedFirstColumn?: boolean;
    fixedLastColumn?: boolean;
    renderSubcomponent: (row: Row<T>) => ReactNode;
}) => {
    const virtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement,
        estimateSize: () => 56,
        overscan: 5,
    });

    /**
     * In some cases `scrollElement` is not mounted yet on a first render. This forces rerender of virtualized rows
     * once the ref is available.
     */
    useEffect(() => {
        virtualizer.measure();
    }, [virtualizer]);

    const virtualRows = virtualizer.getVirtualItems();
    const totalSize = virtualizer.getTotalSize();
    const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
    const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0) : 0;

    return (
        <TableBody
            isLoading={isLoading}
            style={{
                height: totalSize,
                position: 'relative',
            }}
        >
            {paddingTop > 0 && (
                <tr>
                    <td style={{ height: paddingTop }} />
                </tr>
            )}
            {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                const isActive = rowSelection && row.id in rowSelection;
                const nextIsActive = rowSelection && rows[virtualRow.index + 1]?.id in rowSelection;
                const previousIsActive = rowSelection && rows[virtualRow.index - 1]?.id in rowSelection;

                return (
                    <TableRow
                        key={row.id}
                        data-index={virtualRow.index}
                        table={table}
                        row={row}
                        groupDisplayMode={groupDisplayMode}
                        rowSelectionEnabled={rowSelectionEnabled}
                        active={isActive}
                        nextActive={nextIsActive}
                        prevActive={previousIsActive}
                        fixedFirstColumn={fixedFirstColumn}
                        fixedLastColumn={fixedLastColumn}
                        renderSubcomponent={renderSubcomponent}
                    />
                );
            })}
            {paddingBottom > 0 && (
                <tr>
                    <td style={{ height: paddingBottom }} />
                </tr>
            )}
        </TableBody>
    );
};
